import React, { memo } from 'react';
import './MobileStatusWidget.css';

const getBatteryLevelClass = (level) => {
  if (level > 50) return 'battery-level-good';
  if (level > 30) return 'battery-level-warning';
  if (level <= 15) return 'battery-level-critical';
  return 'battery-level-warning';
};

const getGpsSignalClass = (satellites) => {
  if (satellites >= 12) return 'signal-strong';
  if (satellites >= 8) return 'signal-good';
  if (satellites >= 6) return 'signal-fair';
  return 'signal-weak';
};

const formatFlightMode = (mode) => {
  if (!mode) return 'MANUAL';
  
  // Map of official DJI flight modes to their display values
  const modeMap = {
    'MANUAL': 'MANUAL',
    'ATTITUDE': 'ATTI',
    'GPS NORMAL': 'GPS N',
    'POINT OF INTEREST': 'POI',
    'TAKE-OFF READY': 'READY',
    'AUTO TAKE-OFF': 'TOFF',
    'AUTO LANDING': 'LAND',
    'WAYPOINT': 'WAYPT',
    'RTSL': 'RTSL',
    'VIRTUAL STICK': 'VSTK',
    'SMART FLIGHT': 'SMART',
    'PANORAMA': 'PANO',
    'GPS SPORT': 'GPS S',
    'GPS TRIPOD': 'GPS T',
    'AUTO AVOIDANCE': 'AVOID',
    'SMART FLY MODE': 'SMART',
    'FORCE LANDING': 'FLND',
    'ATTITUDE LANDING': 'ALND',
    'CLICK AND GO': 'CLICK',
    'CINEMATIC': 'CINE',
    'DRAW': 'DRAW',
    'FOLLOW ME': 'FOLW',
    'GPS NOVICE': 'GPS NV',
    'QUICK MOVIE': 'QMOV',
    'TAP FLY': 'TAP',
    'MASTER SHOT': 'MSTR',
    'ADVANCED PILOT ASSISTANCE SYSTEM': 'APAS',
    'TIME LAPSE': 'TIME',
    'MOTOR START': 'MOTR',
    'UNKNOWN STATUS': 'UNK'
  };

  // Convert to uppercase for consistent matching
  const upperMode = mode.toUpperCase().trim();
  
  // Try to find a mapped value
  if (modeMap[upperMode]) {
    return modeMap[upperMode];
  }

  // If no direct match, format the string
  // Remove common prefixes
  let formatted = upperMode
    .replace('MODE_', '')
    .replace('FLIGHT_', '')
    .replace('AUTO_', '');

  // Limit to 6 characters to accommodate GPS modes
  if (formatted.length > 6) {
    formatted = formatted.substring(0, 6);
  }

  return formatted;
};

const formatFlightTime = (seconds) => {
  if (!seconds) return '00:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Icon components
const BatteryIcon = ({ level }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="6" width="18" height="10" rx="2" ry="2"></rect>
    <line x1="23" y1="13" x2="23" y2="11"></line>
    {level <= 15 && <line x1="5" y1="10" x2="5" y2="12" strokeWidth="3"></line>}
  </svg>
);

const GpsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const CompassIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
  </svg>
);

const AltitudeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="12 3 12 21"></polyline>
    <polyline points="8 7 12 3 16 7"></polyline>
    <line x1="2" y1="21" x2="22" y2="21"></line>
  </svg>
);

const SpeedVerticalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"></line>
    <polyline points="5 12 12 5 19 12"></polyline>
  </svg>
);

const SpeedHorizontalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 12 12 5 19 12"></polyline>
    <line x1="12" y1="19" x2="12" y2="5"></line>
  </svg>
);

const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

const FlightModeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"></path>
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const MobileStatusWidget = ({ 
  batteryLevel, 
  rcBatteryLevel,
  gpsCount = 0,
  altitude = 0,
  heading = 0,
  verticalSpeed = 0,
  horizontalSpeed = 0,
  flightMode = 'MANUAL',
  distanceFromHome = 0,
  isFlying = false,
  isConnected,
  latitude,
  longitude,
  isFullScreen = false,
  isCompact = false  // For multiview - uses same layout as fullScreen but can be styled differently
}) => {
  // Full-screen layout for telemetry page view
  if (isFullScreen) {
    return (
      <div className="mobile-status-widget-fullscreen">
        {/* Row 1: Power */}
        <div className="telemetry-fullscreen-row">
          <div className="row-header">
            <BatteryIcon level={batteryLevel} />
            <span>POWER</span>
          </div>
          <div className="row-content-horizontal">
            <div className="data-item">
              <span className="data-label">DRONE</span>
              <span className={`data-value ${getBatteryLevelClass(batteryLevel)} ${batteryLevel <= 15 ? 'pulse-text' : ''}`}>
                {batteryLevel}%
              </span>
            </div>
            <div className="data-item">
              <span className="data-label">RC</span>
              <span className={`data-value ${getBatteryLevelClass(rcBatteryLevel)}`}>
                {rcBatteryLevel}%
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: Flight Data */}
        <div className="telemetry-fullscreen-row">
          <div className="row-header">
            <FlightModeIcon />
            <span>FLIGHT DATA</span>
          </div>
          <div className="row-content-horizontal">
            <div className="data-item">
              <span className="data-label">V-SPD</span>
              <span className="data-value">{verticalSpeed.toFixed(1)}m/s</span>
            </div>
            <div className="data-item">
              <span className="data-label">H-SPD</span>
              <span className="data-value">{horizontalSpeed.toFixed(1)}m/s</span>
            </div>
            <div className="data-item">
              <span className="data-label">MODE</span>
              <span className="data-value mode-badge">{formatFlightMode(flightMode)}</span>
            </div>
            <div className="data-item">
              <span className="data-label">HOME</span>
              <span className="data-value">{distanceFromHome.toFixed(0)}m</span>
            </div>
          </div>
        </div>

        {/* Row 3: Navigation */}
        <div className="telemetry-fullscreen-row">
          <div className="row-header">
            <GpsIcon />
            <span>NAVIGATION</span>
          </div>
          <div className="row-content-horizontal">
            <div className="data-item">
              <span className="data-label">GPS</span>
              <div className="data-value-with-icon">
                <div className={`gps-signal ${getGpsSignalClass(gpsCount)}`}>
                  <div className="signal-bar"></div>
                  <div className="signal-bar"></div>
                  <div className="signal-bar"></div>
                  <div className="signal-bar"></div>
                </div>
                <span className="data-value">{gpsCount} sats</span>
              </div>
            </div>
            <div className="data-item">
              <span className="data-label">HDG</span>
              <span className="data-value">{Math.round(heading)}°</span>
            </div>
            <div className="data-item">
              <span className="data-label">STATUS</span>
              <span className={`data-value status-badge ${isFlying ? 'flying' : ''}`}>
                {isFlying ? 'In Flight' : 'Grounded'}
              </span>
            </div>
          </div>
        </div>

        {/* Row 4: Location */}
        <div className="telemetry-fullscreen-row">
          <div className="row-header">
            <LocationIcon />
            <span>LOCATION</span>
            <button 
              className="copy-coords-button-fullscreen"
              onClick={() => {
                const coords = `${latitude ? latitude.toFixed(6) : '0.000000'}, ${longitude ? longitude.toFixed(6) : '0.000000'}`;
                navigator.clipboard.writeText(coords).then(() => {
                  const btn = document.querySelector('.copy-coords-button-fullscreen');
                  if (btn) {
                    btn.classList.add('copied');
                    setTimeout(() => btn.classList.remove('copied'), 1500);
                  }
                }).catch(err => console.error('Failed to copy:', err));
              }}
              aria-label="Copy Coordinates"
              title="Copy Coordinates"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="row-content-horizontal">
            <div className="data-item">
              <span className="data-label">LAT</span>
              <span className="data-value coordinate-value">{latitude ? latitude.toFixed(6) : '0.000000'}</span>
            </div>
            <div className="data-item">
              <span className="data-label">LON</span>
              <span className="data-value coordinate-value">{longitude ? longitude.toFixed(6) : '0.000000'}</span>
            </div>
            <div className="data-item">
              <span className="data-label">ALT</span>
              <span className="data-value altitude-value-large">{altitude.toFixed(1)}m</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular compact layout for footer
  return (
    <div className="mobile-status-widget">
      {/* Row 1: Power + Flight Data */}
      <div className="telemetry-row row-1">
        {/* Power Group */}
        <div className="telemetry-group power-group">
          <div className="group-header">
            <BatteryIcon level={batteryLevel} />
            <span>Power</span>
          </div>
          <div className="group-content">
            <div className="battery-info">
              <div className="battery-label">Drone</div>
              <div className="battery-container">
                <div className="battery-indicator">
                  <div 
                    className="battery-bar"
                    style={{ width: `${batteryLevel}%` }}
                  ></div>
                </div>
                <div className={`battery-text ${getBatteryLevelClass(batteryLevel)} ${batteryLevel <= 15 ? 'pulse-text' : ''}`}>
                  {batteryLevel}%
                </div>
              </div>
            </div>
            <div className="battery-info">
              <div className="battery-label">RC</div>
              <div className="battery-container">
                <div className="battery-indicator">
                  <div 
                    className="battery-bar"
                    style={{ width: `${rcBatteryLevel}%` }}
                  ></div>
                </div>
                <div className={`battery-text ${getBatteryLevelClass(rcBatteryLevel)}`}>
                  {rcBatteryLevel}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flight Data Group */}
        <div className="telemetry-group flight-group">
          <div className="group-header">
            <FlightModeIcon />
            <span>Flight Data</span>
          </div>
          <div className="group-content">
            <div className="info-row">
              <div className="info-item">
                <div className="info-label">
                  <SpeedVerticalIcon />
                  <span>V-SPD</span>
                </div>
                <div className="info-value">
                  <span className="value-text">{verticalSpeed.toFixed(1)}m/s</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <SpeedHorizontalIcon />
                  <span>H-SPD</span>
                </div>
                <div className="info-value">
                  <span className="value-text">{horizontalSpeed.toFixed(1)}m/s</span>
                </div>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item">
                <div className="info-label">MODE</div>
                <div className="info-value flight-mode">{formatFlightMode(flightMode)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <HomeIcon />
                  <span>HOME</span>
                </div>
                <div className="info-value">
                  <span className="value-text">{distanceFromHome.toFixed(0)}m</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Navigation + Location */}
      <div className="telemetry-row row-2">
        {/* Navigation Group */}
        <div className="telemetry-group nav-group">
          <div className="group-header">
            <GpsIcon />
            <span>Navigation</span>
          </div>
          <div className="group-content">
            <div className="info-row">
              <div className="info-item">
                <div className="info-label">GPS</div>
                <div className="info-value">
                  <div className={`gps-signal ${getGpsSignalClass(gpsCount)}`}>
                    <div className="signal-bar"></div>
                    <div className="signal-bar"></div>
                    <div className="signal-bar"></div>
                    <div className="signal-bar"></div>
                  </div>
                  <span className="value-text">{gpsCount} sats</span>
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">
                  <CompassIcon />
                  <span>HDG</span>
                </div>
                <div className="info-value">
                  <span className="value-text">{Math.round(heading)}°</span>
                </div>
              </div>
            </div>
            <div className="info-row">
              <div className="info-item">
                <div className="info-label">Status</div>
                <div className={`info-value status-text ${isFlying ? 'flying' : ''}`}>
                  {isFlying ? 'In Flight' : 'Grounded'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Group */}
        <div className="telemetry-group location-group">
          <div className="group-header">
            <LocationIcon />
            <span>Location</span>
            <button 
              className="copy-coords-button"
              onClick={() => {
                const coords = `${latitude ? latitude.toFixed(6) : '0.000000'}, ${longitude ? longitude.toFixed(6) : '0.000000'}`;
                navigator.clipboard.writeText(coords).then(() => {
                  // Optional: Show a brief toast/feedback
                  const btn = document.querySelector('.copy-coords-button');
                  if (btn) {
                    btn.classList.add('copied');
                    setTimeout(() => btn.classList.remove('copied'), 1500);
                  }
                }).catch(err => console.error('Failed to copy:', err));
              }}
              aria-label="Copy Coordinates"
              title="Copy Coordinates"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="group-content location-content">
            <div className="location-coords">
              <div className="location-item">
                <div className="info-label">LAT</div>
                <div className="info-value">
                  <span className="value-text coordinate">{latitude ? latitude.toFixed(6) : '0.000000'}</span>
                </div>
              </div>
              <div className="location-item">
                <div className="info-label">LON</div>
                <div className="info-value">
                  <span className="value-text coordinate">{longitude ? longitude.toFixed(6) : '0.000000'}</span>
                </div>
              </div>
            </div>
            <div className="location-altitude">
              <div className="info-label">
                <AltitudeIcon />
                <span>ALT</span>
              </div>
              <div className="info-value">
                <span className="value-text altitude-value">{altitude.toFixed(1)}m</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(MobileStatusWidget); 